// Firestore service for Jurisdictions management
import { 
  collection, 
  doc, 
  getDocs, 
  getDoc, 
  setDoc, 
  updateDoc, 
  deleteDoc, 
  query, 
  where, 
  orderBy, 
  onSnapshot,
  serverTimestamp
} from 'firebase/firestore';
import { db } from '../firebase';
import axios from 'axios';
import { censusCountyService } from './censusCountyService';

const COLLECTION_NAME = 'jurisdictions';

// Google Maps API configuration
const GOOGLE_GEOCODING_URL = 'https://maps.googleapis.com/maps/api/geocode/json';

// Helper function to get API key (allows for dynamic reading in tests)
const getGoogleMapsApiKey = () => process.env.REACT_APP_GOOGLE_MAPS_API_KEY;

// Cache configuration
const CACHE_DURATION_MS = 7 * 24 * 60 * 60 * 1000; // 7 days

export const jurisdictionsService = {
  // Get all jurisdictions with real-time updates
  subscribeToJurisdictions: (callback, filters = {}) => {
    let q = collection(db, COLLECTION_NAME);
    
    // Apply filters
    if (filters.country) {
      q = query(q, where('country', '==', filters.country));
    }
    
    if (filters.state) {
      q = query(q, where('state', '==', filters.state));
    }
    
    if (filters.isActive !== undefined) {
      q = query(q, where('isActive', '==', filters.isActive));
    }
    
    // Default ordering by hierarchy
    q = query(q, orderBy('country'), orderBy('state'), orderBy('county'), orderBy('city'));
    
    return onSnapshot(q, (snapshot) => {
      const jurisdictions = [];
      snapshot.forEach((doc) => {
        jurisdictions.push({
          id: doc.id,
          ...doc.data()
        });
      });
      callback(jurisdictions);
    });
  },

  // Get all jurisdictions
  getAll: async (options = {}) => {
    let q = query(
      collection(db, COLLECTION_NAME),
      orderBy('country'),
      orderBy('state'),
      orderBy('county'),
      orderBy('city')
    );

    // Add filters if specified
    if (options.activeOnly) {
      q = query(q, where('isActive', '==', true));
    }

    if (options.country) {
      q = query(q, where('country', '==', options.country));
    }

    if (options.state) {
      q = query(q, where('state', '==', options.state));
    }

    const snapshot = await getDocs(q);
    const jurisdictions = [];
    
    snapshot.forEach((doc) => {
      jurisdictions.push({
        id: doc.id,
        ...doc.data()
      });
    });

    return jurisdictions;
  },

  // Get single jurisdiction
  getJurisdiction: async (jurisdictionId) => {
    const docRef = doc(db, COLLECTION_NAME, jurisdictionId);
    const docSnap = await getDoc(docRef);
    
    if (docSnap.exists()) {
      return {
        id: docSnap.id,
        ...docSnap.data()
      };
    }
    return null;
  },

  // Create new jurisdiction
  createJurisdiction: async (jurisdictionData) => {
    const jurisdictionId = jurisdictionData.id || generateJurisdictionId(jurisdictionData);
    const docRef = doc(db, COLLECTION_NAME, jurisdictionId);
    
    const newJurisdiction = {
      // Required fields with defaults
      country: jurisdictionData.country || 'US',
      state: jurisdictionData.state || '',
      county: jurisdictionData.county || '',
      city: jurisdictionData.city || '',
      zipCode: jurisdictionData.zipCode || '',
      zipCodes: jurisdictionData.zipCodes || [],
      districts: jurisdictionData.districts || [],
      
      // Geographic data
      coordinates: jurisdictionData.coordinates || null,
      boundaries: jurisdictionData.boundaries || null,
      
      // Tax authority information
      taxAuthority: {
        name: '',
        contactInfo: '',
        website: '',
        ...jurisdictionData.taxAuthority
      },
      
      // Metadata
      population: jurisdictionData.population || null,
      areaSquareMiles: jurisdictionData.areaSquareMiles || null,
      establishedDate: jurisdictionData.establishedDate || null,
      source: jurisdictionData.source || 'manual',
      isActive: jurisdictionData.isActive !== undefined ? jurisdictionData.isActive : true,
      
      // Explicitly allowed additional fields only (no tax data)
      ...(jurisdictionData.googleData && { googleData: jurisdictionData.googleData }),
      ...(jurisdictionData.cacheInfo && { cacheInfo: jurisdictionData.cacheInfo }),
      ...(jurisdictionData.originalJurisdiction && { originalJurisdiction: jurisdictionData.originalJurisdiction }),
      
      // Timestamps
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp()
    };
    
    await setDoc(docRef, newJurisdiction);
    return { id: jurisdictionId, ...newJurisdiction };
  },

  // Update existing jurisdiction
  updateJurisdiction: async (jurisdictionId, updateData) => {
    const docRef = doc(db, COLLECTION_NAME, jurisdictionId);
    
    // Only allow legitimate jurisdiction fields to be updated (NO TAX DATA)
    const allowedFields = [
      'country', 'state', 'county', 'city', 'zipCode', 'zipCodes', 'districts',
      'coordinates', 'boundaries', 'taxAuthority', 'population', 'areaSquareMiles',
      'establishedDate', 'source', 'isActive', 'googleData', 'cacheInfo',
      'originalJurisdiction', 'archivedAt'
    ];
    
    const updates = {};
    
    // Only include allowed fields
    allowedFields.forEach(field => {
      if (updateData.hasOwnProperty(field)) {
        updates[field] = updateData[field];
      }
    });
    
    // Always update timestamp
    updates.updatedAt = serverTimestamp();
    
    await updateDoc(docRef, updates);
    return updates;
  },

  // Archive jurisdiction (soft delete)
  archiveJurisdiction: async (jurisdictionId) => {
    const docRef = doc(db, COLLECTION_NAME, jurisdictionId);
    
    await updateDoc(docRef, {
      isActive: false,
      archivedAt: serverTimestamp(),
      updatedAt: serverTimestamp()
    });
  },

  // Restore archived jurisdiction
  restoreJurisdiction: async (jurisdictionId) => {
    const docRef = doc(db, COLLECTION_NAME, jurisdictionId);
    
    await updateDoc(docRef, {
      isActive: true,
      archivedAt: null,
      updatedAt: serverTimestamp()
    });
  },

  // Delete jurisdiction permanently
  deleteJurisdiction: async (jurisdictionId) => {
    const docRef = doc(db, COLLECTION_NAME, jurisdictionId);
    await deleteDoc(docRef);
  },

  // Get jurisdictions by state
  getJurisdictionsByState: async (state) => {
    const q = query(
      collection(db, COLLECTION_NAME),
      where('state', '==', state),
      where('isActive', '==', true),
      orderBy('county'),
      orderBy('city')
    );
    
    const snapshot = await getDocs(q);
    const jurisdictions = [];
    snapshot.forEach((doc) => {
      jurisdictions.push({
        id: doc.id,
        ...doc.data()
      });
    });
    
    return jurisdictions;
  },

  // Search jurisdictions by name
  searchJurisdictions: async (searchTerm) => {
    const snapshot = await getDocs(collection(db, COLLECTION_NAME));
    const jurisdictions = [];
    
    snapshot.forEach((doc) => {
      const data = doc.data();
      const searchString = `${data.state} ${data.county} ${data.city}`.toLowerCase();
      
      if (searchString.includes(searchTerm.toLowerCase())) {
        jurisdictions.push({
          id: doc.id,
          ...data
        });
      }
    });
    
    return jurisdictions;
  },

  // Resolve jurisdiction by address (with Google Maps fallback simulation)
  resolveJurisdiction: async (address) => {
    // First try to find in our database
    const { state, city, zipCode } = parseAddress(address);
    
    // Search by ZIP code first (most specific)
    if (zipCode) {
      const zipQuery = query(
        collection(db, COLLECTION_NAME),
        where('zipCodes', 'array-contains', zipCode)
      );
      const zipSnapshot = await getDocs(zipQuery);
      
      if (!zipSnapshot.empty) {
        const doc = zipSnapshot.docs[0];
        return {
          id: doc.id,
          ...doc.data()
        };
      }
    }
    
    // Search by city and state
    if (city && state) {
      const cityQuery = query(
        collection(db, COLLECTION_NAME),
        where('city', '==', city),
        where('state', '==', state)
      );
      const citySnapshot = await getDocs(cityQuery);
      
      if (!citySnapshot.empty) {
        const doc = citySnapshot.docs[0];
        return {
          id: doc.id,
          ...doc.data()
        };
      }
    }
    
    // Fallback to state level
    if (state) {
      const stateQuery = query(
        collection(db, COLLECTION_NAME),
        where('state', '==', state),
        where('county', '==', ''),
        where('city', '==', '')
      );
      const stateSnapshot = await getDocs(stateQuery);
      
      if (!stateSnapshot.empty) {
        const doc = stateSnapshot.docs[0];
        return {
          id: doc.id,
          ...doc.data()
        };
      }
    }
    
    // If not found, could integrate Google Maps API here
    return null;
  },


  // Get jurisdiction hierarchy (states, then counties, then cities)
  getJurisdictionHierarchy: async () => {
    const snapshot = await getDocs(
      query(collection(db, COLLECTION_NAME), orderBy('state'), orderBy('county'), orderBy('city'))
    );
    
    const hierarchy = {};
    
    snapshot.forEach((doc) => {
      const data = doc.data();
      
      if (!hierarchy[data.state]) {
        hierarchy[data.state] = { counties: {} };
      }
      
      if (data.county && !hierarchy[data.state].counties[data.county]) {
        hierarchy[data.state].counties[data.county] = { cities: [] };
      }
      
      if (data.city && data.county) {
        hierarchy[data.state].counties[data.county].cities.push({
          id: doc.id,
          name: data.city,
          ...data
        });
      }
    });
    
    return hierarchy;
  },

  // Geographic hierarchy methods
  getCountriesList: async () => {
    const snapshot = await getDocs(collection(db, COLLECTION_NAME));
    const countries = new Set();
    
    snapshot.forEach((doc) => {
      const data = doc.data();
      if (data.country) {
        countries.add(data.country);
      }
    });
    
    return Array.from(countries).sort();
  },

  getStatesByCountry: async (country) => {
    const q = query(
      collection(db, COLLECTION_NAME),
      where('country', '==', country),
      where('county', '==', ''),
      where('city', '==', '')
    );
    
    const snapshot = await getDocs(q);
    const states = [];
    
    snapshot.forEach((doc) => {
      states.push({
        id: doc.id,
        ...doc.data()
      });
    });
    
    return states;
  },

  getCountiesByState: async (country, state) => {
    const q = query(
      collection(db, COLLECTION_NAME),
      where('country', '==', country),
      where('state', '==', state),
      where('city', '==', '')
    );
    
    const snapshot = await getDocs(q);
    const counties = [];
    
    snapshot.forEach((doc) => {
      const data = doc.data();
      if (data.county) { // Only include records with actual county data
        counties.push({
          id: doc.id,
          ...data
        });
      }
    });
    
    return counties;
  },

  getCitiesByCounty: async (country, state, county) => {
    const q = query(
      collection(db, COLLECTION_NAME),
      where('country', '==', country),
      where('state', '==', state),
      where('county', '==', county)
    );
    
    const snapshot = await getDocs(q);
    const cities = [];
    
    snapshot.forEach((doc) => {
      const data = doc.data();
      if (data.city) { // Only include records with actual city data
        cities.push({
          id: doc.id,
          ...data
        });
      }
    });
    
    return cities;
  },

  // Enhanced search methods with Google Maps integration
  searchByZipCode: async (zipCode) => {
    // First, search in local Firestore database
    const localResults = await jurisdictionsService.searchLocalByZipCode(zipCode);
    
    if (localResults.length > 0) {
      return {
        success: true,
        source: 'local',
        results: localResults
      };
    }
    
    // If no local results and Google Maps API is available, try geocoding
    if (getGoogleMapsApiKey()) {
      try {
        const googleResult = await jurisdictionsService.geocodeZipCode(zipCode);
        
        if (googleResult.success && googleResult.data) {
          // Cache the Google result in Firestore for future use
          await jurisdictionsService.cacheGoogleResult(zipCode, googleResult.data);
          
          return {
            success: true,
            source: 'google',
            results: [googleResult.data],
            cached: true
          };
        }
      } catch (error) {
        console.warn('Google geocoding failed:', error.message);
      }
    }
    
    return {
      success: false,
      source: 'none',
      results: [],
      message: 'ZIP code not found in local database or Google Maps'
    };
  },
  
  // Local database search (original functionality)
  searchLocalByZipCode: async (zipCode) => {
    // Search in zipCode field
    const zipQuery = query(
      collection(db, COLLECTION_NAME),
      where('zipCode', '==', zipCode)
    );
    
    // Search in zipCodes array
    const zipArrayQuery = query(
      collection(db, COLLECTION_NAME),
      where('zipCodes', 'array-contains', zipCode)
    );
    
    const [zipSnapshot, zipArraySnapshot] = await Promise.all([
      getDocs(zipQuery),
      getDocs(zipArrayQuery)
    ]);
    
    const results = [];
    const seenIds = new Set();
    
    // Add results from zipCode field
    zipSnapshot.forEach((doc) => {
      if (!seenIds.has(doc.id)) {
        results.push({
          id: doc.id,
          ...doc.data()
        });
        seenIds.add(doc.id);
      }
    });
    
    // Add results from zipCodes array
    zipArraySnapshot.forEach((doc) => {
      if (!seenIds.has(doc.id)) {
        results.push({
          id: doc.id,
          ...doc.data()
        });
        seenIds.add(doc.id);
      }
    });
    
    return results;
  },
  
  // Google Maps ZIP code geocoding
  geocodeZipCode: async (zipCode) => {
    const apiKey = getGoogleMapsApiKey();
    if (!apiKey) {
      return {
        success: false,
        message: 'Google Maps API key not configured'
      };
    }
    
    try {
      const response = await axios.get(GOOGLE_GEOCODING_URL, {
        params: {
          address: zipCode + ', USA', // Force USA in address
          key: apiKey,
          region: 'us', // Bias results to US
          components: 'country:US' // Restrict to US only
        },
        timeout: 5000
      });
      
      if (response.data.status === 'OK' && response.data.results.length > 0) {
        const result = response.data.results[0];
        const jurisdiction = jurisdictionsService.parseGoogleResult(result, zipCode);
        
        // Enhanced logging for debugging unknown counties
        console.log(`🔍 Google geocoding for ZIP ${zipCode}:`, {
          status: response.data.status,
          formatted_address: result.formatted_address,
          location_type: result.geometry?.location_type,
          parsed_county: jurisdiction.county,
          all_components: result.address_components?.map(c => ({
            name: c.long_name,
            short: c.short_name,
            types: c.types
          }))
        });
        
        if (!jurisdiction.county || jurisdiction.county === 'Unknown County') {
          console.warn(`⚠️ UNKNOWN COUNTY for ZIP ${zipCode}:`, {
            google_response: result,
            parsed_data: jurisdiction
          });
        }
        
        return {
          success: true,
          data: jurisdiction,
          rawGoogleData: result
        };
      } else {
        console.error(`❌ Google geocoding failed for ZIP ${zipCode}:`, {
          status: response.data.status,
          error: response.data.error_message
        });
        return {
          success: false,
          message: `Google geocoding failed: ${response.data.status}`,
          error: response.data.error_message
        };
      }
    } catch (error) {
      return {
        success: false,
        message: 'Google geocoding request failed',
        error: error.message
      };
    }
  },
  
  // Parse Google geocoding result into jurisdiction format
  parseGoogleResult: (googleResult, zipCode) => {
    const addressComponents = googleResult.address_components || [];
    const geometry = googleResult.geometry || {};
    
    let country = 'US';
    let state = '';
    let county = '';
    let city = '';
    
    // Parse address components
    addressComponents.forEach(component => {
      const types = component.types || [];
      
      if (types.includes('country')) {
        country = component.short_name;
      } else if (types.includes('administrative_area_level_1')) {
        state = component.short_name;
      } else if (types.includes('administrative_area_level_2')) {
        county = component.long_name.replace(' County', '');
      } else if (types.includes('locality') || types.includes('sublocality')) {
        city = component.long_name;
      }
    });
    
    // Census fallback for missing county data
    if (!county || county === '' || county === 'Unknown County') {
      const censusCounty = censusCountyService.getCountyForZipCode(zipCode);
      if (censusCounty) {
        county = censusCounty;
        console.log(`📍 Census fallback: ZIP ${zipCode} → ${censusCounty} County (Google had no county data)`);
      } else {
        county = 'Unknown County';
      }
    }
    
    // Generate jurisdiction ID
    const jurisdictionId = jurisdictionsService.generateJurisdictionId({
      country,
      state,
      county,
      city,
      zipCode
    });
    
    return {
      id: jurisdictionId,
      country,
      state,
      county,
      city,
      zipCode,
      zipCodes: [zipCode],
      districts: [],
      coordinates: {
        lat: geometry.location?.lat || null,
        lng: geometry.location?.lng || null
      },
      boundaries: geometry.bounds ? {
        northeast: geometry.bounds.northeast,
        southwest: geometry.bounds.southwest
      } : null,
      taxAuthority: {
        name: '',
        contactInfo: '',
        website: ''
      },
      population: null,
      areaSquareMiles: null,
      establishedDate: null,
      source: 'google_geocoding',
      isActive: true,
      googleData: {
        placeId: googleResult.place_id,
        formattedAddress: googleResult.formatted_address,
        cachedAt: new Date().toISOString()
      }
    };
  },
  
  // Cache Google result in Firestore
  cacheGoogleResult: async (zipCode, jurisdictionData) => {
    try {
      const docRef = doc(db, COLLECTION_NAME, jurisdictionData.id);
      
      const cacheData = {
        ...jurisdictionData,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
        cacheInfo: {
          originalZipCode: zipCode,
          cachedAt: serverTimestamp(),
          expiresAt: new Date(Date.now() + CACHE_DURATION_MS)
        }
      };
      
      await setDoc(docRef, cacheData);
      
      console.log(`Cached Google geocoding result for ZIP ${zipCode}`);
      return true;
    } catch (error) {
      console.error('Failed to cache Google result:', error);
      return false;
    }
  },

  searchByLocation: async (searchTerm) => {
    const snapshot = await getDocs(collection(db, COLLECTION_NAME));
    const results = [];
    
    snapshot.forEach((doc) => {
      const data = doc.data();
      const searchString = `${data.state} ${data.county} ${data.city}`.toLowerCase();
      
      if (searchString.includes(searchTerm.toLowerCase())) {
        results.push({
          id: doc.id,
          ...data
        });
      }
    });
    
    return results;
  },

  // Validation method
  validateJurisdiction: (jurisdictionData) => {
    const errors = [];
    
    // Required fields validation
    if (!jurisdictionData.country || jurisdictionData.country.trim() === '') {
      errors.push('Country is required');
    }
    
    if (!jurisdictionData.state || jurisdictionData.state.trim() === '') {
      errors.push('State is required');
    }
    
    // Tax authority validation
    if (jurisdictionData.taxAuthority) {
      if (!jurisdictionData.taxAuthority.name || jurisdictionData.taxAuthority.name.trim() === '') {
        errors.push('Tax authority name is required');
      }
      
      // Additional validation can be added here for contactInfo, website, etc.
    }
    
    // ZIP code validation
    if (jurisdictionData.zipCode && !/^\d{5}(-\d{4})?$/.test(jurisdictionData.zipCode)) {
      errors.push('Invalid ZIP code format');
    }
    
    // Population validation
    if (jurisdictionData.population !== null && jurisdictionData.population !== undefined && 
        (jurisdictionData.population < 0 || !Number.isInteger(jurisdictionData.population))) {
      errors.push('Population must be a non-negative integer');
    }
    
    // Area validation
    if (jurisdictionData.areaSquareMiles !== null && jurisdictionData.areaSquareMiles !== undefined && 
        jurisdictionData.areaSquareMiles < 0) {
      errors.push('Area must be non-negative');
    }
    
    return {
      isValid: errors.length === 0,
      errors
    };
  },

  // Google Maps integration (enhanced implementations)
  geocodeAddress: async (address) => {
    const apiKey = getGoogleMapsApiKey();
    if (!apiKey) {
      return {
        success: false,
        message: 'Google Maps API key not configured',
        data: null
      };
    }
    
    try {
      const response = await axios.get(GOOGLE_GEOCODING_URL, {
        params: {
          address: address,
          key: apiKey
        },
        timeout: 5000
      });
      
      if (response.data.status === 'OK' && response.data.results.length > 0) {
        const result = response.data.results[0];
        
        return {
          success: true,
          data: {
            formattedAddress: result.formatted_address,
            coordinates: {
              lat: result.geometry.location.lat,
              lng: result.geometry.location.lng
            },
            placeId: result.place_id,
            addressComponents: result.address_components,
            geometry: result.geometry
          },
          rawResponse: result
        };
      } else {
        return {
          success: false,
          message: `Geocoding failed: ${response.data.status}`,
          data: null
        };
      }
    } catch (error) {
      return {
        success: false,
        message: `Geocoding request failed: ${error.message}`,
        data: null
      };
    }
  },

  validateWithGoogleMaps: async (jurisdiction) => {
    const apiKey = getGoogleMapsApiKey();
    if (!apiKey) {
      return {
        success: false,
        message: 'Google Maps API key not configured',
        validated: false,
        suggestions: []
      };
    }
    
    try {
      // Construct address from jurisdiction data
      const addressParts = [
        jurisdiction.city,
        jurisdiction.county ? `${jurisdiction.county} County` : '',
        jurisdiction.state,
        jurisdiction.country === 'US' ? 'USA' : jurisdiction.country,
        jurisdiction.zipCode
      ].filter(Boolean);
      
      const address = addressParts.join(', ');
      const geocodeResult = await jurisdictionsService.geocodeAddress(address);
      
      if (geocodeResult.success) {
        const suggestions = [];
        const googleComponents = geocodeResult.data.addressComponents;
        
        // Validate and suggest corrections
        googleComponents.forEach(component => {
          const types = component.types;
          
          if (types.includes('administrative_area_level_1')) {
            if (component.short_name !== jurisdiction.state) {
              suggestions.push({
                field: 'state',
                current: jurisdiction.state,
                suggested: component.short_name,
                confidence: 'high'
              });
            }
          }
          
          if (types.includes('administrative_area_level_2')) {
            const googleCounty = component.long_name.replace(' County', '');
            if (googleCounty !== jurisdiction.county) {
              suggestions.push({
                field: 'county',
                current: jurisdiction.county,
                suggested: googleCounty,
                confidence: 'medium'
              });
            }
          }
        });
        
        return {
          success: true,
          validated: suggestions.length === 0,
          suggestions,
          geocodeData: geocodeResult.data
        };
      } else {
        return {
          success: false,
          message: 'Could not validate with Google Maps',
          validated: false,
          suggestions: []
        };
      }
    } catch (error) {
      return {
        success: false,
        message: `Validation failed: ${error.message}`,
        validated: false,
        suggestions: []
      };
    }
  },

  enrichWithGoogleMapsData: async (jurisdictionId) => {
    const apiKey = getGoogleMapsApiKey();
    if (!apiKey) {
      return {
        success: false,
        message: 'Google Maps API key not configured',
        enrichedData: null
      };
    }
    
    try {
      // Get the jurisdiction from Firestore
      const jurisdiction = await jurisdictionsService.getJurisdiction(jurisdictionId);
      
      if (!jurisdiction) {
        return {
          success: false,
          message: 'Jurisdiction not found',
          enrichedData: null
        };
      }
      
      // Use existing coordinates or geocode address
      let coordinates = jurisdiction.coordinates;
      
      if (!coordinates || !coordinates.lat || !coordinates.lng) {
        const addressParts = [
          jurisdiction.city,
          jurisdiction.county ? `${jurisdiction.county} County` : '',
          jurisdiction.state,
          jurisdiction.zipCode
        ].filter(Boolean);
        
        const geocodeResult = await jurisdictionsService.geocodeAddress(addressParts.join(', '));
        
        if (geocodeResult.success) {
          coordinates = geocodeResult.data.coordinates;
        } else {
          return {
            success: false,
            message: 'Could not get coordinates for enrichment',
            enrichedData: null
          };
        }
      }
      
      // For now, return the coordinates and note that place details would require additional API calls
      const enrichedData = {
        coordinates,
        enrichmentDate: new Date().toISOString(),
        note: 'Full place details enrichment requires Google Places API integration'
      };
      
      // Update the jurisdiction with enriched data
      await jurisdictionsService.updateJurisdiction(jurisdictionId, {
        coordinates,
        googleEnriched: true,
        lastEnriched: new Date().toISOString()
      });
      
      return {
        success: true,
        enrichedData,
        message: 'Jurisdiction enriched with Google Maps coordinates'
      };
    } catch (error) {
      return {
        success: false,
        message: `Enrichment failed: ${error.message}`,
        enrichedData: null
      };
    }
  },

  // Utility method exposed for tests
  generateJurisdictionId: (jurisdictionData) => {
    const parts = [
      jurisdictionData.country || 'US',
      jurisdictionData.state || '',
      jurisdictionData.county || '',
      jurisdictionData.city || '',
      jurisdictionData.zipCode || ''
    ].filter(Boolean);
    
    return parts.join('_').toUpperCase().replace(/\s+/g, '_');
  }
};

// Helper functions
function generateJurisdictionId(jurisdictionData) {
  const parts = [
    jurisdictionData.country || 'US',
    jurisdictionData.state || '',
    jurisdictionData.county || '',
    jurisdictionData.city || ''
  ].filter(Boolean);
  
  return parts.join('_').toUpperCase().replace(/\s+/g, '_');
}

function parseAddress(address) {
  // Simple address parsing - in production, use a proper address parsing library
  const parts = address.split(',').map(part => part.trim());
  
  // This is a simplified parser - real implementation would be more robust
  return {
    state: parts[parts.length - 2]?.split(' ')[0] || '',
    city: parts[parts.length - 3] || '',
    county: '',
    zipCode: parts[parts.length - 1]?.match(/\d{5}/)?.[0] || ''
  };
}

export default jurisdictionsService;